import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCircle, X, AlertCircle, Info, AlertTriangle, CheckSquare } from 'react-feather';
import { useNotifications } from '../contexts/NotificationContext';
import { useResponsive } from '../hooks/useResponsive';
import { useConfirm } from '../hooks/useConfirm';

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();

  const { ConfirmComponent: DeleteAllConfirm, confirm: confirmDeleteAll } = useConfirm({
    title: 'Delete all notifications?',
    message: 'This permanently removes every notification in your list. This cannot be undone.',
    confirmText: 'Delete all',
    cancelText: 'Cancel',
    variant: 'danger',
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Auto-mark all as read when dropdown opens
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  }, [isOpen, unreadCount, markAllAsRead]);

  const handleNotificationClick = (e: React.MouseEvent, notification: any) => {
    // Don't trigger if clicking delete button
    if ((e.target as HTMLElement).closest('button[data-delete]')) {
      return;
    }

    // Navigate if there's a link
    if (notification.link) {
      // Check if it's an external link (starts with http)
      if (notification.link.startsWith('http')) {
        window.open(notification.link, '_blank', 'noopener,noreferrer');
      } else {
        // Internal navigation
        navigate(notification.link);
      }
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const handleDelete = (e: React.MouseEvent, notificationId: number) => {
    e.preventDefault();
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  const handleDeleteAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirmDeleteAll();
    if (!confirmed) return;
    await deleteAllNotifications();
  };

  const getNotificationIcon = (type: string) => {
    const iconProps = { size: 20, strokeWidth: 2.5 };
    switch (type) {
      case 'success':
        return <CheckCircle {...iconProps} color="#10b981" />;
      case 'warning':
        return <AlertTriangle {...iconProps} color="#f59e0b" />;
      case 'error':
        return <AlertCircle {...iconProps} color="#ef4444" />;
      default:
        return <Info {...iconProps} color="#3b82f6" />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <DeleteAllConfirm />
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        /* The bell is an icon with no text, so it reached a screen reader as an
           unnamed button on every page of the product (axe: button-name,
           critical). Attributes only — nothing about it renders differently. */
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        style={{
          width: '40px',
          height: '40px',
          border: 'none',
          background: isOpen ? '#f0f4ff' : '#fafbff',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.background = '#f0f4ff';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.background = '#fafbff';
        }}
      >
        <Bell size={20} color="#64748b" />
        {unreadCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '18px',
              height: '18px',
              background: '#ef4444',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '700',
              color: 'white',
              padding: '0 4px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </button>

                {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '60px' : '48px',
            right: isMobile ? '8px' : '0',
            left: isMobile ? '8px' : 'auto',
            width: isMobile ? 'auto' : '380px',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '480px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            zIndex: 1000,
            fontFamily: 'Manrope, sans-serif'
          }}
        >
          {/* Header with Delete All button */}
          {notifications.length > 0 && (
            <div
              style={{
                padding: '12px 20px',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={handleDeleteAll}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#ef4444',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Manrope, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Delete all
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div
            style={{
              maxHeight: isMobile ? 'calc(100vh - 180px)' : '400px',
              overflowY: 'auto',
              paddingBottom: '8px'
            }}
          >
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: '48px 20px',
                  textAlign: 'center'
                }}
              >
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    margin: '0 auto 16px',
                    background: '#f9fafb',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Bell size={28} color="#cbd5e1" />
                </div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#151133' }}>
                  No notifications yet
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                  We'll notify you when something happens
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={(e) => handleNotificationClick(e, notification)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: notification.link ? 'pointer' : 'default',
                    background: 'white',
                    transition: 'background 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (notification.link) {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          gap: '8px'
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#151133',
                            flex: 1
                          }}
                        >
                          {notification.title}
                        </h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#94a3b8',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatTime(notification.createdAt)}
                          </span>
                          <button
                            data-delete="true"
                            onClick={(e) => handleDelete(e, notification.id)}
                            style={{
                              padding: '4px',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              opacity: 0.5
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f3f4f6';
                              e.currentTarget.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.opacity = '0.5';
                            }}
                          >
                            <X size={14} color="#64748b" />
                          </button>
                        </div>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '13px',
                          color: '#64748b',
                          lineHeight: '1.5'
                        }}
                      >
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
